<template>
  <!-- renderless: all UI is driven through the F7 toast API -->
  <span v-if="false" />
</template>

<script setup>
import { f7ready } from 'framework7-vue';
import { watch, onMounted, onUnmounted } from 'vue';
import { useNetworkDetector } from '@/composables/useNetworkDetector.js';

const { isOnline } = useNetworkDetector();

let offlineToast = null;
let onlineToast  = null;
let wentOffline  = false;

const f7 = () => window.f7Instance ?? null;

// ─── reactive watcher ────────────────────────────────────────────────────────
// NOT immediate — initial offline state is handled below via f7ready,
// which guarantees F7 exists before we try to open a toast.
watch(isOnline, (online) => {
  if (online) {
    closeOfflineToast();
    if (wentOffline) {
      wentOffline = false;
      openOnlineToast();
    }
  } else {
    wentOffline = true;
    openOfflineToast();
  }
});

// ─── initial offline state ───────────────────────────────────────────────────
onMounted(() => {
  f7ready(() => {
    if (!isOnline.value) {
      wentOffline = true;
      openOfflineToast();
    }
  });
});

// ─── offline toast ────────────────────────────────────────────────────────────
function openOfflineToast() {
  if (!f7() || offlineToast) return;

  offlineToast = f7().toast.create({
    text: '📵 لا يوجد اتصال بالإنترنت',
    position: 'bottom',
    closeButton: true,
    closeButtonText: 'إعادة',
    cssClass: 'nst-offline-toast',
    on: {
      // Retry: close the toast — the composable's interval / next browser
      // event will re-check and flip isOnline → the watcher handles the rest.
      closeButtonClick: closeOfflineToast,
    },
  });

  offlineToast.open();
}

function closeOfflineToast() {
  offlineToast?.close();
  offlineToast?.destroy();
  offlineToast = null;
}

// ─── online toast ─────────────────────────────────────────────────────────────
function openOnlineToast() {
  closeOfflineToast();
  onlineToast?.close();
  onlineToast?.destroy();

  onlineToast = f7().toast.create({
    text: '✅ عاد الاتصال بالإنترنت',
    position: 'bottom',
    closeTimeout: 3_000,
    cssClass: 'nst-online-toast',
    on: {
      close() {
        onlineToast?.destroy();
        onlineToast = null;
      },
    },
  });

  onlineToast.open();
}

// ─── cleanup ──────────────────────────────────────────────────────────────────
onUnmounted(() => {
  closeOfflineToast();
  onlineToast?.close();
  onlineToast?.destroy();
  onlineToast = null;
});
</script>

<style>
.toast.nst-offline-toast,
.toast.nst-online-toast {
    margin-bottom: calc(
        var(--f7-tabbar-labels-height, 49px) +
        var(--f7-safe-area-bottom, 0px) +
        8px
    );
}

.toast.nst-offline-toast {
    --f7-toast-bg-color: #1c1c1e;
    --f7-toast-text-color: #fff;
    border-radius: 24px;
    max-width: 360px;
}

.toast.nst-online-toast {
    --f7-toast-bg-color: #10B981;
    --f7-toast-text-color: #fff;
    border-radius: 24px;
    max-width: 300px;
}

.toast.nst-offline-toast .toast-button {
    color: #a78bfa;
    font-weight: 700;
}
</style>
